import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';

// Create axios instance
const getApiBaseUrl = () => {
  // Check if we're in production (not localhost)
  if (window.location.hostname === 'ai.dream-digi.com' || window.location.hostname === 'www.ai.dream-digi.com') {
    return 'https://api.dream-digi.com/api';
  }
  // Check for environment variable
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  // Default to localhost for development
  return 'http://localhost:5001/api';
};

const apiBaseUrl = getApiBaseUrl();
console.log('🚀 API Base URL:', apiBaseUrl);
console.log('🚀 Current hostname:', window.location.hostname);
console.log('🚀 Environment VITE_API_URL:', import.meta.env.VITE_API_URL);

const api = axios.create({
  baseURL: apiBaseUrl,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to track if we're in the middle of a logout
let isLoggingOut = false;

// Function to set logout state
export const setLogoutState = (state: boolean) => {
  isLoggingOut = state;
};

// Function to get logout state
export const getLogoutState = () => {
  return isLoggingOut;
};

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token && !isLoggingOut) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    const { response } = error;

    // Handle different error scenarios
    if (response) {
      const { status, data } = response;

      switch (status) {
        case 401:
          // Unauthorized - clear token and redirect to login
          if (!isLoggingOut) {
            isLoggingOut = true;
            localStorage.removeItem('token');
            delete api.defaults.headers.common['Authorization'];
            
            // Only show toast and redirect if not already on auth page and not during logout
            const currentPath = window.location.pathname;
            const isOnAuthPage = currentPath.includes('/auth') || currentPath.includes('/login') || currentPath.includes('/register');
            
            if (!isOnAuthPage) {
              toast.error('Session expired. Please log in again.');
              // Use replace instead of href to prevent back button issues
              window.location.replace('/login');
            }
            
            // Reset the flag after a short delay
            setTimeout(() => {
              isLoggingOut = false;
            }, 1000);
          }
          break;

        case 403:
          // Forbidden
          toast.error(data.message || 'Access denied');
          break;

        case 404:
          // Not found
          toast.error(data.message || 'Resource not found');
          break;

        case 422:
          // Validation error
          if (data.details && Array.isArray(data.details)) {
            const errors = data.details.map((err: any) => err.msg).join(', ');
            toast.error(errors);
          } else {
            toast.error(data.message || 'Validation failed');
          }
          break;

        case 429:
          // Rate limited
          toast.error('Too many requests. Please try again later.');
          break;

        case 500:
          // Server error
          toast.error('Server error. Please try again later.');
          break;

        default:
          // Other errors
          toast.error(data.message || 'An error occurred');
          break;
      }
    } else if (error.request) {
      // Network error
      toast.error('Network error. Please check your connection.');
    } else {
      // Other error
      toast.error('An unexpected error occurred');
    }

    return Promise.reject(error);
  }
);

// Generate unique request ID
const generateRequestId = (): string => {
  return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// API methods
export const apiService = {
  // Auth endpoints
  auth: {
    login: (data: { email: string; password: string }) =>
      api.post('/auth/login', data),
    
    register: (data: {
      email: string;
      password: string;
      websiteUrl: string;
      phoneNumber: string;
      firstName?: string;
      lastName?: string;
      company?: string;
    }) => api.post('/auth/register', data),
    
    logout: () => api.post('/auth/logout'),
    
    getProfile: () => api.get('/auth/me'),
    
    updateProfile: (data: {
      firstName?: string;
      lastName?: string;
      company?: string;
      phoneNumber?: string;
    }) => api.put('/auth/profile', data),
    
    forgotPassword: (email: string) =>
      api.post('/auth/forgot-password', { email }),
    
    resetPassword: (data: { token: string; newPassword: string }) =>
      api.post('/auth/reset-password', data),
    
    verifyEmail: (token: string) =>
      api.post('/auth/verify-email', { token }),
  },

  // Pages endpoints
  pages: {
    getAll: (params?: {
      page?: number;
      limit?: number;
      status?: string;
      search?: string;
    }) => api.get('/pages', { params }),
    
    getById: (id: string) => api.get(`/pages/${id}`),
    
    capture: (data: { url: string; html: string; title?: string }) =>
      api.post('/pages/capture', data),
    
    optimize: (id: string) => api.post(`/pages/${id}/optimize`),
    
    update: (id: string, data: {
      title?: string;
      notes?: string;
      tags?: string[];
      isPublic?: boolean;
      optimizedHtml?: string;
    }) => api.put(`/pages/${id}`, data),
    
    delete: (id: string) => api.delete(`/pages/${id}`),
    
    revert: (id: string, version: number) =>
      api.post(`/pages/${id}/revert/${version}`),
    
    getSuggestions: (id: string) => api.get(`/pages/${id}/suggestions`),
    
    getAnalytics: () => api.get('/pages/analytics/summary'),
  },

  // Snippets endpoints
  snippets: {
    getSnippet: (userId: string) => api.get(`/snippets/${userId}`),
    
    getEmbedCode: (userId: string) => api.get(`/snippets/${userId}/embed`),
    
    getTestPage: (userId: string) => api.get(`/snippets/${userId}/test`),
    
    getStatus: (userId: string) => api.get(`/snippets/${userId}/status`),
  },

  // Subscriptions endpoints
  subscriptions: {
    getStatus: () => api.get('/subscriptions'),
    
    create: (data: { plan: string; paymentMethodId: string }) =>
      api.post('/subscriptions/create', data),
    
    cancel: () => api.post('/subscriptions/cancel'),
    
    update: (data: { plan: string }) => api.put('/subscriptions', data),
  },

  // Admin endpoints
  admin: {
    getAnalytics: (params?: { days?: number }) =>
      api.get('/admin/analytics', { params }),
    
    getUsers: (params?: {
      page?: number;
      limit?: number;
      search?: string;
      status?: string;
    }) => api.get('/admin/users', { params }),
    
    getUser: (id: string) => api.get(`/admin/users/${id}`),
    
    updateUser: (id: string, data: any) => api.put(`/admin/users/${id}`, data),
    
    deleteUser: (id: string) => api.delete(`/admin/users/${id}`),
    
    getActivities: (params?: {
      page?: number;
      limit?: number;
      userId?: string;
      type?: string;
    }) => api.get('/admin/activities', { params }),
    
    getHealth: () => api.get('/admin/health'),
    
    getProviders: () => api.get('/admin/providers'),
    
    activateEmailProvider: (provider: string, data: any) => 
      api.post(`/admin/providers/email/${provider}`, data),
    
    activatePaymentGateway: (gateway: string, data: any) => 
      api.post(`/admin/providers/payment/${gateway}`, data),
    
    sendNotification: (data: any) => api.post('/admin/notifications', data),
    
    getSettings: () => api.get('/admin/settings'),
    
    updateSettings: (data: any) => api.put('/admin/settings', data),
  },

  // Health check
  health: () => api.get('/health'),
};

// Export the axios instance for direct use if needed
export default api;

// Export types
export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  error?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface Page {
  id: string;
  url: string;
  title: string;
  status: 'captured' | 'processing' | 'optimized' | 'error';
  seoAnalysis: {
    score: number;
    issues: Array<{
      type: 'error' | 'warning' | 'info';
      message: string;
      element: string;
      suggestion: string;
    }>;
    improvements: Array<{
      category: string;
      description: string;
      impact: 'high' | 'medium' | 'low';
    }>;
  };
  geoAnalysis: {
    score: number;
    suggestions: Array<{
      type: string;
      content: string;
      placement: string;
      priority: 'high' | 'medium' | 'low';
    }>;
    keywords: Array<{
      keyword: string;
      density: number;
      position: string;
    }>;
  };
  performanceAnalysis: {
    score: number;
    loadTime: number;
    issues: Array<{
      type: string;
      description: string;
      impact: string;
      solution: string;
    }>;
    recommendations: Array<{
      category: string;
      description: string;
      potentialSavings: string;
    }>;
  };
  analytics: {
    views: number;
    lastViewed?: string;
    optimizationCount: number;
    lastOptimized?: string;
  };
  tags: string[];
  notes?: string;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  websiteUrl: string;
  phoneNumber: string;
  company?: string;
  role: 'user' | 'admin' | 'super_admin';
  subscription: {
    status: 'free' | 'active' | 'cancelled' | 'expired';
    plan: 'free' | 'basic' | 'pro' | 'enterprise';
    pageLimit: number;
    pagesUsed: number;
    currentPeriodEnd?: string;
  };
  isEmailVerified: boolean;
  preferences: {
    notifications: {
      email: boolean;
      dashboard: boolean;
    };
    theme: 'light' | 'dark' | 'auto';
  };
  createdAt: string;
  lastLogin?: string;
}

export interface SnippetData {
  snippetUrl: string;
  embedCode: string;
  instructions: string[];
  user: {
    email: string;
    websiteUrl: string;
  };
}

export interface SubscriptionData {
  status: 'free' | 'active' | 'cancelled' | 'expired';
  plan: 'free' | 'basic' | 'pro' | 'enterprise';
  pageLimit: number;
  pagesUsed: number;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
}